import { supabase } from '../lib/supabase';

/**
 * Module Real-time Synchronization Service
 * Manages real-time subscriptions for module updates and budget changes
 * across all production managers
 */
const moduleRealtimeService = {
  /**
   * Subscribe to module changes for a specific project
   * Triggers callback when modules are created, updated, or deleted
   */
  subscribeToModuleChanges(projectId, callback) {
    const channel = supabase
      ?.channel(`project_modules_${projectId}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public', 
          table: 'project_products',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            type: 'module_change',
            event: payload?.eventType, // INSERT, UPDATE, DELETE
            data: payload?.new || payload?.old,
            oldData: payload?.old
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to budget changes for a specific project
   * Triggers callback when budget data is updated
   */
  subscribeToBudgetChanges(projectId, callback) {
    const channel = supabase
      ?.channel(`project_budget_${projectId}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public', 
          table: 'project_budget',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            type: 'budget_change',
            event: payload?.eventType,
            data: payload?.new || payload?.old
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to project progress changes
   * Triggers callback when progress data is updated
   */
  subscribeToProgressChanges(projectId, callback) {
    const channel = supabase
      ?.channel(`project_progress_${projectId}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public', 
          table: 'project_progress',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            type: 'progress_change',
            event: payload?.eventType,
            data: payload?.new || payload?.old
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to project resources changes
   * Triggers callback when resource data is updated
   */
  subscribeToResourceChanges(projectId, callback) {
    const channel = supabase
      ?.channel(`project_resources_${projectId}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public', 
          table: 'project_resources',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            type: 'resource_change',
            event: payload?.eventType,
            data: payload?.new || payload?.old
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to project QA/QC changes
   * Triggers callback when QA/QC data is updated
   */
  subscribeToQAQCChanges(projectId, callback) {
    const channel = supabase
      ?.channel(`project_qaqc_${projectId}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public', 
          table: 'project_qaqc',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            type: 'qaqc_change',
            event: payload?.eventType,
            data: payload?.new || payload?.old
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to project HSE changes
   * Triggers callback when HSE data is updated
   */
  subscribeToHSEChanges(projectId, callback) {
    const channel = supabase
      ?.channel(`project_hse_${projectId}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public', 
          table: 'project_hse',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            type: 'hse_change',
            event: payload?.eventType,
            data: payload?.new || payload?.old
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to project cashflow changes
   * Triggers callback when cashflow data is updated
   */
  subscribeToCashflowChanges(projectId, callback) {
    const channel = supabase
      ?.channel(`project_cashflow_${projectId}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public', 
          table: 'project_cashflow',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            type: 'cashflow_change',
            event: payload?.eventType,
            data: payload?.new || payload?.old
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to project risks changes
   * Triggers callback when risk data is updated
   */
  subscribeToRiskChanges(projectId, callback) {
    const channel = supabase
      ?.channel(`project_risks_${projectId}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public', 
          table: 'project_risks',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            type: 'risk_change',
            event: payload?.eventType,
            data: payload?.new || payload?.old
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Subscribe to project contacts changes
   * Triggers callback when contact data is updated
   */
  subscribeToContactChanges(projectId, callback) {
    const channel = supabase
      ?.channel(`project_contacts_${projectId}`)
      ?.on(
        'postgres_changes',
        { 
          event: '*',
          schema: 'public', 
          table: 'project_contacts',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback({
            type: 'contact_change',
            event: payload?.eventType,
            data: payload?.new || payload?.old
          });
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from a channel
   */
  async unsubscribe(channel) {
    if (channel) {
      await supabase?.removeChannel(channel);
    }
  },

  /**
   * Unsubscribe from multiple channels
   */
  async unsubscribeAll(channels) {
    if (channels && Array.isArray(channels)) {
      for (const channel of channels) {
        await this.unsubscribe(channel);
      }
    }
  }
};

export default moduleRealtimeService;